import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
	EmailTemplatePreviewRequest,
	EmailTemplatePreviewResponse,
	EmailTemplateToken,
	SiteToken
} from '@sleed/bizcommerce-api';
import { emailTemplatesApi, sitesApi } from '~/lib/api/client';
import { formToToken, type EmailTemplateFormValues } from '~/lib/schemas/email-template';

export const emailTemplateKeys = {
	all: ['email-templates'] as const,
	list: () => [...emailTemplateKeys.all, 'list'] as const,
	detail: (id: string) => [...emailTemplateKeys.all, 'detail', id] as const,
	sites: () => [...emailTemplateKeys.all, 'sites'] as const
};

export function useEmailTemplatesList() {
	return useQuery<EmailTemplateToken[]>({
		queryKey: emailTemplateKeys.list(),
		queryFn: async () => (await emailTemplatesApi.getAll()).data.items ?? []
	});
}

/**
 * Single template for the editor page. `GetAll` omits the body + recipients, so the editor must
 * load the full record by id (mirrors `fetchEmailTemplate` / the legacy `byKey: api_getById`).
 * Disabled for the `create` route (non-numeric id) so a blank form is used instead.
 */
export function useEmailTemplate(id: string) {
	const numericId = Number(id);
	const enabled = Number.isFinite(numericId) && numericId > 0;
	return useQuery<EmailTemplateToken | null>({
		queryKey: emailTemplateKeys.detail(id),
		queryFn: async () => (enabled ? (await emailTemplatesApi.getById(numericId)).data : null),
		enabled
	});
}

export function useEmailTemplateSites() {
	return useQuery<SiteToken[]>({
		queryKey: emailTemplateKeys.sites(),
		queryFn: async () => (await sitesApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

/**
 * Fetch a single template by id. `GetAll` omits the heavy `bd` (body) and recipient fields, so
 * the editor must load the full record (matches the legacy `byKey: api_getById`) — otherwise a
 * save would persist an empty body.
 */
export async function fetchEmailTemplate(id: number): Promise<EmailTemplateToken> {
	return (await emailTemplatesApi.getById(id)).data;
}

export function useEmailTemplateMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: emailTemplateKeys.list() });
	const create = useMutation({
		mutationFn: async (form: EmailTemplateFormValues) => (await emailTemplatesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: EmailTemplateFormValues) => (await emailTemplatesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await emailTemplatesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}

/** Renders the (unsaved) draft with sample data through the real template engine; nothing is saved or sent. */
export function useEmailTemplatePreview() {
	return useMutation<EmailTemplatePreviewResponse, Error, EmailTemplatePreviewRequest>({
		mutationFn: async request => (await emailTemplatesApi.preview(request)).data
	});
}

/** The model a real send of `code` would render for the order, or null when the order does not exist. */
export async function fetchOrderPreviewModel(orderId: number, code: string): Promise<Record<string, unknown> | null> {
	const { data } = await emailTemplatesApi.orderPreviewModel(orderId, code);
	return data.found ? ((data.model as Record<string, unknown> | undefined) ?? {}) : null;
}
